/* Display formatting. Money arrives as decimal strings; floats only ever touch the display layer. */
import type { AnyCurrency, Estimate, Money } from "../api/types";

/* Currencies with a symbol put it first ("€12.00", "⭐ 500"); the rest put the code after the
   number, the way wallets write them ("15.97 TON", "20.00 USDT"). */
const SYMBOL: Record<string, string> = { EUR: "€", USD: "$", XTR: "⭐ " };

/** Attach the currency to an already formatted number: "€12.00", "15.97 TON". */
export function withCurrency(body: string | number, currency: AnyCurrency, sign = ""): string {
  const pre = SYMBOL[currency];
  return pre != null ? `${sign}${pre}${body}` : `${sign}${body} ${currency}`;
}
/** "€1.52–€1.67", "0.39–0.93 TON". */
export function moneyRange(low: string | number, high: string | number, currency: AnyCurrency, opts: { decimals?: number } = {}): string {
  const lo = moneyOf(low, currency, opts);
  const hi = moneyOf(high, currency, opts);
  if (SYMBOL[currency] != null) return `${lo}–${hi}`;
  return `${lo.replace(` ${currency}`, "")}–${hi}`;
}

/** "€1,284.20", "15.97 TON". Pass `sign` for signed deltas ("+€12.00", "−4.10 TON"). */
export function money(m: Money | null | undefined, opts: { sign?: boolean; compact?: boolean; decimals?: number } = {}): string {
  if (!m) return "—";
  return moneyOf(m.amount, m.currency, opts);
}
export function moneyOf(amount: string | number, currency: AnyCurrency, opts: { sign?: boolean; compact?: boolean; decimals?: number } = {}): string {
  const n = typeof amount === "number" ? amount : parseFloat(amount);
  if (!isFinite(n)) return "—";
  const abs = Math.abs(n);
  const decimals = opts.decimals ?? (currency === "XTR" ? 0 : opts.compact && abs >= 1000 ? 1 : 2);
  let body: string;
  if (opts.compact && abs >= 1000) {
    body = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: decimals }).format(abs);
  } else {
    body = abs.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  const sign = n < 0 ? "−" : opts.sign && n > 0 ? "+" : "";
  return withCurrency(body, currency, sign);
}
export const num = (n: number | null | undefined, compact = false) =>
  n == null ? "—" : compact && Math.abs(n) >= 10000 ? new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n) : n.toLocaleString("en-US");
export const pct = (r: number | null | undefined, digits = 1) => (r == null ? "—" : `${(r * 100).toFixed(digits)}%`);
export const amountOf = (m: Money | null | undefined) => (m ? parseFloat(m.amount) : 0);
export const sym = (c: AnyCurrency) => (SYMBOL[c] ?? c).trim();

export function plural(n: number, one: string, many = one + "s") {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

/** "just now", "5 min ago", "3 h ago", "yesterday", "12 Sep". */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const s = Math.round((now - t) / 1000);
  if (s < 0) return until(iso, now);
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 172800) return "yesterday";
  if (s < 7 * 86400) return `${Math.round(s / 86400)} days ago`;
  return day(iso);
}
export function until(iso: string, now = Date.now()): string {
  const s = Math.round((new Date(iso).getTime() - now) / 1000);
  if (s <= 0) return "now";
  if (s < 3600) return `in ${Math.max(1, Math.round(s / 60))} min`;
  if (s < 86400) return `in ${Math.round(s / 3600)} h`;
  return `in ${Math.round(s / 86400)} days`;
}
/** Telegram's chat-list timestamp: time today, weekday this week, date otherwise. */
export function chatTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  const d = new Date(iso);
  const n = new Date(now);
  if (d.toDateString() === n.toDateString()) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (now - d.getTime() < 6 * 86400_000) return d.toLocaleDateString("en-US", { weekday: "short" });
  return dayOf(d);
}
/* Three-letter months everywhere ("24 Sep"); newer ICU writes "Sept" for en-GB. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "24 Sep" from a Date or ISO string. */
export const dayOf = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
export const day = (iso: string) => dayOf(new Date(iso));
export const dateTime = (iso: string) => {
  const d = new Date(iso);
  return `${dayOf(d)}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};
export const clock = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

/** Human label for an Estimate's confidence. Thin data is shown, flagged — never hidden. */
export function confidenceLabel(e: Pick<Estimate, "confidence" | "n"> | null | undefined, unit = "results"): string {
  if (!e) return "No data";
  switch (e.confidence) {
    case "high":
      return `High confidence · ${plural(e.n, unit.replace(/s$/, ""), unit)}`;
    case "medium":
      return `Medium confidence · ${plural(e.n, unit.replace(/s$/, ""), unit)}`;
    case "low":
      return `Low confidence · only ${plural(e.n, unit.replace(/s$/, ""), unit)}`;
    default:
      return `Not enough data · ${plural(e.n, unit.replace(/s$/, ""), unit)}`;
  }
}
export const confidenceScore = (c: Estimate["confidence"] | null | undefined) =>
  c === "high" ? 0.9 : c === "medium" ? 0.62 : c === "low" ? 0.3 : 0.08;

/** Estimate → "€2.14" or "€2.14 (€1.60–€3.10)"; "2.14 TON (1.60–3.10 TON)". */
export function estimate(e: Estimate | null | undefined, currency?: AnyCurrency, kind: "money" | "ratio" | "x" = "money", withRange = false): string {
  if (!e || e.value == null) return "—";
  const f = (v: string) => (kind === "money" && currency ? moneyOf(v, currency) : kind === "ratio" ? pct(parseFloat(v)) : `${parseFloat(v).toFixed(2)}×`);
  const main = f(e.value);
  if (!withRange || e.low == null || e.high == null) return main;
  return `${main} (${kind === "money" && currency ? moneyRange(e.low, e.high, currency) : `${f(e.low)}–${f(e.high)}`})`;
}

/** UTF-8 byte length — Telegram Ads limits titles in bytes (spec 6.1). */
export const utf8Bytes = (s: string) => new TextEncoder().encode(s).length;
/** Telegram counts ad text in characters (code points). */
export const charCount = (s: string) => [...s].length;

export const initials = (name: string) =>
  name
    .replace(/^@/, "")
    .split(/[\s_·-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("") || "?";

/** Telegram's avatar palette, picked by a stable hash like the clients do. */
const AVATAR = ["#e17076", "#faa774", "#a695e7", "#7bc862", "#6ec9cb", "#65aadd", "#ee7aae"];
export function avatarColor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR[Math.abs(h) % AVATAR.length]!;
}

export const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
